import { useEffect, useRef, useState } from "react";
import { LuPause, LuPlay } from "react-icons/lu";
import { calm } from "./fx";
import { GameArt } from "./GameArt";

/** short silent clips of every game being played live in `public/previews/<game>.mp4`; made by `tools/capture/previews.ts` */
export const previewSrc = (game: string) => `/previews/${game}.mp4`;
/** the five v16 games have no recorded film yet (record them with `tools/capture/previews.ts`, then remove the game from this list) – they keep their illustration only */
const NO_FILM = new Set(["ludo", "yatzy", "sudoku", "videnspar", "verden"]);
export const hasFilm = (game: string) => !NO_FILM.has(game);

/** true when it is fine to start a video on its own: no "reduce motion" and no "data saver" */
export function mayAutoplay(): boolean {
  try {
    if (calm()) return false;
    const c = (navigator as any).connection;
    return !(c && (c.saveData || /(^|-)2g$/.test(c.effectiveType ?? "")));
  } catch {
    return true;
  }
}

/**
 * The player on a game's page. At rest it shows the game's own illustration; the short film of the game (silent, looping) plays ONLY while
 * the mouse is over the picture, and stops – back to the picture – when the mouse leaves. On touch screens the round play button starts and
 * stops it; with reduced motion or data saver nothing starts by itself. If the film is missing the illustration simply stays.
 */
export function GameVideo({ game, name }: { game: string; name: string }) {
  const [on, setOn] = useState(false);
  if (!hasFilm(game)) {
    return (
      <figure className="gvideo" data-tilt="3">
        <div className="gv-screen"><GameArt game={game} className="gv-art" title={`Illustration: ${name}`} /></div>
      </figure>
    );
  }
  const enter = (e: { pointerType?: string }) => { if (e.pointerType === "mouse" && mayAutoplay()) setOn(true); };
  const leave = (e: { pointerType?: string }) => { if (e.pointerType === "mouse") setOn(false); };
  return (
    <figure className={`gvideo${on ? " is-playing" : ""}`} data-tilt="3" onPointerEnter={enter} onPointerLeave={leave}>
      <div className="gv-screen">
        <GameArt game={game} className="gv-art" title={`Illustration: ${name}`} />
        <TileVideo game={game} on={on} />
        <button type="button" className="gv-btn" aria-pressed={on} onClick={() => setOn((v) => !v)} aria-label={on ? `Stop videoen af ${name}` : `Afspil en kort video (uden lyd) af ${name}`}>
          {on ? <LuPause aria-hidden="true" size={22} /> : <LuPlay aria-hidden="true" size={22} />}
        </button>
        <span className="gv-chip" aria-hidden="true"><LuPlay size={13} /><b className="gv-hov">Hold musen her</b><b className="gv-tap">Tryk og se</b></span>
      </div>
    </figure>
  );
}

/**
 * The film layer: nothing is downloaded until `on` becomes true for the first time; then the clip fades in over the artwork and,
 * when `on` turns false again, it stops, rewinds and fades out (the artwork is always underneath).
 */
export function TileVideo({ game, on }: { game: string; on: boolean }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [ready, setReady] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [dead, setDead] = useState(false);
  useEffect(() => { if (on) setMounted(true); }, [on]);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (on) v.play().catch(() => {});
    else { v.pause(); try { v.currentTime = 0; } catch { /* not ready yet */ } setReady(false); }
  }, [on, mounted]);
  if (!mounted || dead || !hasFilm(game)) return null;
  return (
    <video ref={ref} src={previewSrc(game)} className={`gt-vid${ready && on ? " ready" : ""}`} muted loop playsInline preload="auto" onPlaying={() => setReady(true)} onError={() => setDead(true)} aria-hidden="true" tabIndex={-1} />
  );
}
